/**
 * @file Instance Axios dédiée aux widgets embarquables, authentifiée par clé API.
 * @module packages.common.utils.axiosWidget
 *
 * Variante autonome de l'instance principale (`axios.js`), adaptée à un bundle
 * embarqué sur des sites tiers. Différences clés avec `axios.js` :
 * - Auth par header `x-api-key` (clé publique `pub_*`) au lieu d'un cookie de session.
 * - `withCredentials: false` : aucun cookie n'est envoyé ni reçu (contexte cross-origin).
 * - Aucune fin de session ni redirection : sur 401/403, l'erreur est seulement
 *   journalisée et propagée, le widget hôte décide de la suite.
 * - Endpoints distincts (`widgetEndpoints`, préfixe `/v1`) au lieu de `endpoints`.
 * - Journalisation systématique des requêtes/réponses via `widgetLogger`.
 *
 * @see axios.js Instance principale (session par cookie, fin de session déclarée par le serveur).
 */

import axios from 'axios';
import {HOST_API} from 'apps/dashboard/ConfigGlobal';
import {widgetLogger, authLogger} from 'packages/common/utils/widgetLogger';
const axiosWidgetInstance = axios.create({
  baseURL: HOST_API,
  withCredentials: false, // Pas de cookies pour les widgets
});

/**
 * Construit les options Axios pour une requête authentifiée par API key.
 *
 * @param {string} apiKey - Clé API publique (doit commencer par `pub_`)
 * @return {Object} Options Axios avec header `x-api-key`, ou objet vide si clé invalide
 */
export const withApiKey = (apiKey) => {
  if (!apiKey) {
    authLogger.missingApiKey();
    return {};
  }

  if (apiKey && !apiKey.startsWith('pub_')) {
    authLogger.invalidApiKey(apiKey);
    return {};
  }

  return {
    headers: {
      'x-api-key': apiKey,
    },
  };
};

/** Endpoints spécifiques aux widgets (API externe V1 uniquement). */
export const widgetEndpoints = {
  externalApi: {
    // Miroir exact du public (`endpoints.public.restaurant.reservation`) : même
    // structure `{module}/{resource}/:companyId/{action}`, seuls diffèrent le
    // préfixe (/v1 vs /public) et l'auth (clé API vs slug). Le widget n'a que la
    // création (pas de gestion par token, propre au site public).
    restaurant: {
      reservation: {
        config: (companyId) => `/v1/restaurant/reservation/${companyId}/config`,
        dates: (companyId) => `/v1/restaurant/reservation/${companyId}/dates`,
        nextDates: (companyId) => `/v1/restaurant/reservation/${companyId}/next-dates`,
        timeslots: (companyId) => `/v1/restaurant/reservation/${companyId}/timeslots`,
        rooms: (companyId) => `/v1/restaurant/reservation/${companyId}/rooms`,
        create: (companyId) => `/v1/restaurant/reservation/${companyId}/create`,
        evaluateCardHold: (companyId) => `/v1/restaurant/reservation/${companyId}/evaluate-card-hold`,
        preparePayment: (companyId) => `/v1/restaurant/reservation/${companyId}/prepare-payment`,
        confirmPayment: (companyId) => `/v1/restaurant/reservation/${companyId}/confirm-payment`,
        waitlist: (companyId) => `/v1/restaurant/reservation/${companyId}/waitlist`,
        draft: (companyId) => `/v1/restaurant/reservation/${companyId}/draft`,
        formula: {
          available: (companyId) => `/v1/restaurant/reservation/${companyId}/formula/available`,
          dates: (companyId) => `/v1/restaurant/reservation/${companyId}/formula/dates`,
        },
      },
    },
    giftVoucher: {
      types: (companyId) => `/v1/gift-vouchers/catalog/${companyId}`,
      categories: (companyId) => `/v1/gift-vouchers/categories/${companyId}`,
      catalog: '/v1/gift-vouchers/catalog', // Sans companyId
      categoriesGeneral: '/v1/gift-vouchers/categories', // Sans companyId
      customVoucherConfig: '/v1/gift-vouchers/custom-voucher/config', // Sans companyId
    },
    company: {
      details: (companyId) => `/v1/company/${companyId}`,
      widgetConfig: '/v1/company/widget-config',
    },
  },
};

/** Intercepteur de requêtes : log le verbe HTTP et la présence de l'API key. */
axiosWidgetInstance.interceptors.request.use(
    (config) => {
      const hasApiKey = !!config.headers?.['x-api-key'];
      widgetLogger.api.request(config.method || 'GET', config.url || '', hasApiKey);
      return config;
    },
    (error) => {
      return Promise.reject(error);
    },
);

/** Intercepteur de réponses : log les erreurs sans redirection. */
axiosWidgetInstance.interceptors.response.use(
    (response) => {
      const method = response.config?.method || 'GET';
      const url = response.config?.url || '';
      widgetLogger.api.success(method, url, response.status);
      return response;
    },

    async (error) => {
      const status = error.response?.status;
      const config = error.config;
      const method = config?.method || 'GET';
      const url = config?.url || '';

      widgetLogger.api.error(method, url, status, error);

      switch (status) {
        case 401:
          authLogger.authenticationError();
          break;
        case 403:
          authLogger.accessDenied();
          break;
        case 429:
          const retryAfter = error.response.headers['retry-after'];
          widgetLogger.warn(`Rate limit atteint${retryAfter ? ` - Réessayer dans ${retryAfter}s` : ''}`);
          break;
        case 404:
          // 404 sur custom-voucher/config = feature non activée, pas une erreur
          if (!error.config?.url?.includes('/custom-voucher/config')) {
            widgetLogger.warn('Ressource non trouvée');
          }
          break;
        default:
          widgetLogger.error('Erreur API', error.message);
      }

      return Promise.reject(error);
    },
);

export default axiosWidgetInstance;
